import { APP_INITIALIZER, ApplicationConfig, EnvironmentProviders, importProvidersFrom, LOCALE_ID, Provider, provideZoneChangeDetection } from '@angular/core';
import { provideRouter } from '@angular/router';
import { TranslateHttpLoader } from '@ngx-translate/http-loader';

import { ConfigProjectService } from './services/config.service';
import { ProjectConfigService } from './services/project-config.service';
import { AuthService } from './services/auth.service';
import { register } from 'swiper/element/bundle';
import { DatePipe, DecimalPipe, registerLocaleData } from '@angular/common';
import { InitializeAppService } from './services/initialize-app.service';
import { HTTP_INTERCEPTORS, HttpClient, provideHttpClient, withInterceptorsFromDi } from '@angular/common/http';
import { TokenInterceptorService } from './services/interceptor.service';
import { provideAnimations } from '@angular/platform-browser/animations';
import { provideToastr } from 'ngx-toastr';
import { provideAnimationsAsync } from '@angular/platform-browser/animations/async';
import { routes } from './main.routes';
import localeEs from '@angular/common/locales/es';
import { HttpClientProvider } from './services/http-client';
import { CustomToastComponent } from './components/custom-toast/custom-toast.component';
import { provideCharts, withDefaultRegisterables } from 'ng2-charts';
import { CalendarModule, DateAdapter } from 'angular-calendar';
import { adapterFactory } from 'angular-calendar/date-adapters/date-fns';
import { provideFlatpickrDefaults } from 'angularx-flatpickr';
import { provideTranslateService, TranslateLoader, TranslateModule } from '@ngx-translate/core';
import { provideCheckoutComponents } from '@solunes/solunes-checkout';
import { environment } from '../environments/environment';
import { globalConfig } from './global-config';
register();
registerLocaleData(localeEs, 'es-ES');

function HttpLoaderFactory(http: HttpClient) {
  return new TranslateHttpLoader(http, './assets/i18n/', '.json');
}

function initializeApp(projectConfigService: ProjectConfigService, authService: AuthService) {
  return async () => {
    await projectConfigService.loadConfig();
    await authService.getUserByToken();
  };
}

function getDynamicProviders(): (Provider|EnvironmentProviders)[] {
  const providers: (Provider|EnvironmentProviders)[] = [
    provideHttpClient(
      withInterceptorsFromDi()
    ),
    DecimalPipe,
    DatePipe,
    InitializeAppService,
    ConfigProjectService,
    importProvidersFrom(HttpClientProvider),
    importProvidersFrom(AuthService),
    provideToastr({
      toastComponent: CustomToastComponent,
      newestOnTop: false,
    }),
    provideCharts(withDefaultRegisterables()),
    { provide: HTTP_INTERCEPTORS, useClass: TokenInterceptorService, multi: true },
    { provide: LOCALE_ID, useValue: 'es-ES' },
    {
      provide: TranslateLoader,
      useFactory: HttpLoaderFactory,
      deps: [HttpClient]
    },
    provideTranslateService({
      defaultLanguage: 'es'
    }),
    {
      provide: DateAdapter,
      useFactory: adapterFactory
    },
    provideFlatpickrDefaults(),
    provideZoneChangeDetection({ eventCoalescing: true }),
    {
      provide: APP_INITIALIZER,
      useFactory: initializeApp,
      deps: [ProjectConfigService, AuthService],
      multi: true
    },
    provideAnimations(),
    provideAnimationsAsync(),
  ];


  // console.log('Configuración del proyecto:', window.__project);

  // Validation - ADD BRIDGE
  if(window.__project?.config?.bridge || globalConfig.bridge) {

    // console.log(window.__project?.config?.bridge);
    // console.log(globalConfig.bridge);
    // Start - Configuración para Solunes Checkout
    const isDebug = window.__project?.env?.production ? window.__project?.env?.production === false : !environment.production
    providers.unshift(
      provideCheckoutComponents({
        apiUrl: window.__project?.config?.bridge?.apiUrl ?? globalConfig.bridge?.apiUrl,
        tenantId: window.__project?.env?.type ?? environment.type,
        tenantUrl: window.__project?.env?.apiUrl ?? environment.apiUrl,
        xApiKey: window.__project?.config?.bridge?.xApiKey ?? globalConfig.bridge?.xApiKey ?? '',
        xSecretKey: window.__project?.config?.bridge?.xSecretKey ?? globalConfig.bridge?.xSecretKey ?? '',
        enableDebug: isDebug,
        enablePos: window.__project?.config?.bridge?.enabledPOS ?? globalConfig.bridge?.enabledPOS ?? false,
        otherParams: {
          profile: {
            createDefault: true,
          },
          paymentProcess: {
            ui: 'simple',
            billingDetailsTitle: 'Datos de pago',
          }
        }
      })
    );
    // End - Configuración para Solunes Checkout
  }

  // Añadir GTM solo si existe en la configuración
  if (window.__project?.env?.gtmId) {
    providers.push({
      provide: 'googleTagManagerId',
      useValue: window.__project?.env?.gtmId
    });
  } else {
    console.warn('GTM ID no configurado. Omitiendo proveedor.');
  }

  return providers;
};

export function configureAppWithProjectConfig(projectConfig: any) {
  const dynamicProviders = getDynamicProviders();

  // console.log('Configuración del proyecto (en app.config):', projectConfig);

  // Validation - ADD BRIDGE (ahora con la configuración disponible)
  if(projectConfig?.config?.bridge || globalConfig.bridge) {
    const isDebug = projectConfig?.env?.production ? projectConfig?.env?.production === false : !environment.production;

    dynamicProviders.unshift(
      provideCheckoutComponents({
        apiUrl: projectConfig?.config?.bridge?.apiUrl ?? globalConfig.bridge?.apiUrl,
        tenantId: projectConfig?.env?.type ?? environment.type,
        tenantUrl: projectConfig?.env?.apiUrl ?? environment.apiUrl,
        xApiKey: projectConfig?.config?.bridge?.xApiKey ?? globalConfig.bridge?.xApiKey ?? '',
        xSecretKey: projectConfig?.config?.bridge?.xSecretKey ?? globalConfig.bridge?.xSecretKey ?? '',
        enableDebug: isDebug,
        enablePos: projectConfig?.config?.bridge?.enabledPOS ?? globalConfig.bridge?.enabledPOS ?? false,
        otherParams: {
          profile: { createDefault: true },
          paymentProcess: {
            ui: 'simple',
            billingDetailsTitle: 'Datos de pago'
          }
        }
      })
    );
  }

  return {
    providers: [
      ...dynamicProviders,
      provideZoneChangeDetection({ eventCoalescing: true }),
      provideRouter(routes)
    ]
  };
}

// export const appConfig: ApplicationConfig = {
//   providers: [
//     ...getDynamicProviders(),
//     provideZoneChangeDetection({ eventCoalescing: true }),
//     provideRouter(routes)
//   ]
// };
